import api from "@/api";
import i18n from "@/i18n";
import {
  createLatestRequestGate,
  parseApiError,
  parseCollectionAuthorization,
  teamQuery,
  teamScopedCacheKey,
  teamContextRequiredError,
} from "@/helpers/teamAuthorization";

const { t } = i18n.global;
const customerRequestGate = createLatestRequestGate();
const customerFormsRequestGate = createLatestRequestGate();
const userRequestGate = createLatestRequestGate();
const policyRequestGate = createLatestRequestGate();
const policyFormsRequestGate = createLatestRequestGate();
const leadRequestGate = createLatestRequestGate();
const leadFormsRequestGate = createLatestRequestGate();
const potentialCustomerRequestGate = createLatestRequestGate();
const potentialCustomerFormsRequestGate = createLatestRequestGate();

export default {
  state: {
    searchCustomerForms: {},
    searchPotentialCustomerForms: {},
    potentialCustomersLoading: false,
    potentialCustomerCollectionAuthorization: null,
    potentialCustomerCollectionError: null,
    searchLeadForms: {},

    policiesPerPage: {},
    currentPoliciesPage: 1,
    policiesLoading: false,
    searchPoliciesForms: {}, 
    policyCollectionError: null,
  },
  getters: {
    searchCustomerForms: (state) => state.searchCustomerForms,
    searchPotentialCustomerForms: (state) => state.searchPotentialCustomerForms,
    potentialCustomersLoading: (state) => state.potentialCustomersLoading,
    potentialCustomerCollectionAuthorization: (state) => state.potentialCustomerCollectionAuthorization,
    potentialCustomerCollectionError: (state) => state.potentialCustomerCollectionError,
    searchLeadForms: (state) => state.searchLeadForms,

    policiesPerPage: (state) => state.policiesPerPage,
    currentPoliciesPage: (state) => state.currentPoliciesPage,
    policiesLoading: (state) => state.policiesLoading,

    searchPoliciesForms: (state) => state.searchPoliciesForms, 
    policyCollectionError: (state) => state.policyCollectionError,
  },
  mutations: {
    setSearchCustomerForms: (state, data) => {
      state.searchCustomerForms = data;
    },
    setSearchPotentialCustomerForms: (state, data) => {
      state.searchPotentialCustomerForms = data;
    },
    setPotentialCustomersLoading: (state, data) => {
      state.potentialCustomersLoading = data;
    },
    setPotentialCustomerCollectionAuthorization: (state, data) => {
      state.potentialCustomerCollectionAuthorization = data;
    },
    setPotentialCustomerCollectionError: (state, data) => {
      state.potentialCustomerCollectionError = data;
    },
    setSearchLeadForms: (state, data) => {
      state.searchLeadForms = data;
    },

    setPoliciesPerPage: (state, data) => {
      state.policiesPerPage = data;
    },
    setCurrentPoliciesPage: (state, data) => {
      state.currentPoliciesPage = data;
    },

    setPoliciesLoading: (state, data) => {
      state.policiesLoading = data;
    },

    setSearchPoliciesForms: (state, data) => {
      state.searchPoliciesForms = data;
    },
 
    setPolicyCollectionError: (state, data) => {
      state.policyCollectionError = data;
    },
  },
  actions: {
    searchPotentialCustomer: async (_, phone) =>
      new Promise((resolve, reject) => {
        api({ requiresAuth: true })
          .get(`/v1/filter/potential-customers?phone=${phone}`)
          .then(
            (response) => {
              const [data] = response.data.data;
              resolve(data);
            },
            (err) => {
              reject(err);
            }
          );
      }),

    searchUsers: async ({ commit, dispatch, rootGetters }, {
      search = "",
      active_users = null,
      page = 1,
      teamId = null,
      isExport = false,
    }) => {
      const requestKey = teamScopedCacheKey({
        endpoint: "/v1/users",
        teamId,
        query: { search, active_users, page, isExport },
        actorId: rootGetters.loggedUser?.id,
      });
      const { signal } = userRequestGate.start(requestKey);
      if (!isExport) {
        commit("setUsersLoading", true);
        commit("setUsersPerPage", { data: [] });
        commit("setUserCollectionAuthorization", null);
        commit("setUserCollectionError", null);
      }

      try {
        if (isExport && Object.keys(teamQuery(teamId)).length === 0) {
          throw teamContextRequiredError();
        }

        const normalizedTeamIds = Array.isArray(teamId) ? teamId : (teamId !== null && teamId !== undefined ? [teamId] : []);
        const normalizedActiveUsers = active_users === null || active_users === undefined || active_users === ""
          ? null
          : Number(active_users);

        const hasFilters = Boolean(search)
          || (normalizedActiveUsers !== null && Number.isInteger(normalizedActiveUsers))
          || normalizedTeamIds.length > 0;

        const requestConfig = {
          params: { page, ...teamQuery(teamId), ...(isExport ? { export: true } : {}) },
          responseType: isExport ? "blob" : "json",
          signal,
        };
        const response = hasFilters || isExport
          ? await api({ requiresAuth: true }).post("/v1/search/users", {
              searchKeywords: search,
              active_users: normalizedActiveUsers,
            }, requestConfig)
          : await api({ requiresAuth: true }).get("/v1/users", requestConfig);

        if (!userRequestGate.isActive(requestKey)) return;

        if (isExport) {
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          const fileURL = window.URL.createObjectURL(blob);
          const fileLink = document.createElement("a");
          fileLink.href = fileURL;
          fileLink.setAttribute("download", "Users_export.xlsx");
          document.body.appendChild(fileLink);
          fileLink.click();
          document.body.removeChild(fileLink);
          window.URL.revokeObjectURL(fileURL);
        } else {
          commit("setUsersPerPage", response.data);
          commit("setUserCollectionAuthorization", parseCollectionAuthorization(response));
        }
      } catch (err) {
        if (err.code === "ERR_CANCELED" || !userRequestGate.isActive(requestKey)) return;
        if (!isExport) commit("setUserCollectionError", parseApiError(err));
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToSearchUsers"),
        });
      } finally {
        if (!isExport && userRequestGate.isActive(requestKey)) commit("setUsersLoading", false);
      }
    },

    getSearchCustomerForms: async ({ commit, dispatch }, requestedTeamId) => {
      let teamIds = Array.isArray(requestedTeamId) ? requestedTeamId : [requestedTeamId];
      teamIds = teamIds.map(Number).filter(id => Number.isInteger(id) && id > 0);

      commit("setSearchCustomerForms", {});
      if (teamIds.length === 0) return;

      const requestKey = `customer-forms:${teamIds.join(',')}`;
      const { signal } = customerFormsRequestGate.start(requestKey);
      try {
        const {
          data: { data },
        } = await api({ requiresAuth: true }).get(
          "/v1/partials/search-customers",
          { params: teamQuery(teamIds), signal }
        );

        if (customerFormsRequestGate.isActive(requestKey)) {
          commit("setSearchCustomerForms", data);
        }
      } catch (err) {
        if (err.code === "ERR_CANCELED" || !customerFormsRequestGate.isActive(requestKey)) return;
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToLoadSearchCustomerForms"),
        });
      }
    },

    getSearchAddFamilyMemberForms: async ({ commit, dispatch }) => {
      try {
        const {
          data: { data },
        } = await api({ requiresAuth: true }).get(
          "/v1/partials/search-family"
        );
        commit("setSearchCustomerForms", data);
      } catch (err) {
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToLoadFamilySearchForms"),
        });
      }
    },

    getSearchPotentialCustomerForms: async ({ commit, dispatch }, requestedTeamId) => {
      let teamIds = Array.isArray(requestedTeamId) ? requestedTeamId : [requestedTeamId];
      teamIds = teamIds.map(Number).filter(id => Number.isInteger(id) && id > 0);
      commit("setSearchPotentialCustomerForms", {});
      if (teamIds.length === 0) return;

      const requestKey = `potential-customer-forms:${teamIds.join(',')}`;
      const { signal } = potentialCustomerFormsRequestGate.start(requestKey);
      try {
        const {
          data: { data },
        } = await api({ requiresAuth: true }).get(
          "/v1/partials/search-potential-customers",
          { params: teamQuery(teamIds), signal }
        );
        if (potentialCustomerFormsRequestGate.isActive(requestKey)) {
          commit("setSearchPotentialCustomerForms", data);
        }
      } catch (err) {
        if (err.code === "ERR_CANCELED" || !potentialCustomerFormsRequestGate.isActive(requestKey)) return;
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToLoadPotentialCustomerForms"),
        });
      }
    },

    getSearchLeadForms: async ({ commit, dispatch }, requestedTeamId) => {
      let teamIds = Array.isArray(requestedTeamId) ? requestedTeamId : [requestedTeamId];
      teamIds = teamIds.map(Number).filter(id => Number.isInteger(id) && id > 0);
      commit("setSearchLeadForms", {});
      if (teamIds.length === 0) return;

      const requestKey = `lead-forms:${teamIds.join(',')}`;
      const { signal } = leadFormsRequestGate.start(requestKey);
      try {
        const {
          data: { data },
        } = await api({ requiresAuth: true }).get(
          "/v1/partials/search-leads",
          { params: teamQuery(teamIds), signal }
        );

        if (leadFormsRequestGate.isActive(requestKey)) commit("setSearchLeadForms", data);
      } catch (err) {
        if (err.code === "ERR_CANCELED" || !leadFormsRequestGate.isActive(requestKey)) return;
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToLoadLeadForms"),
        });
      }
    },

    searchPotentialCustomers: async ({ commit, dispatch, rootGetters }, { page = 1, teamId = null, ...searchData }) => {
      const requestKey = teamScopedCacheKey({
        endpoint: "/v1/potential-customers",
        teamId,
        query: { page, ...searchData },
        actorId: rootGetters.loggedUser?.id,
      });
      const { signal } = potentialCustomerRequestGate.start(requestKey);
      commit("setPotentialCustomersLoading", true);
      commit("setCustomersPerPage", { data: [] });
      commit("setPotentialCustomerCollectionAuthorization", null);
      commit("setPotentialCustomerCollectionError", null);
      try {
        const hasTeamScope = Array.isArray(teamId) ? teamId.length > 0 : !!teamId;
        const isSearching = hasTeamScope || Object.values(searchData).some(val =>
          (Array.isArray(val) ? val.length > 0 : (val !== "" && val !== null && val !== undefined))
        );

        let response;
        if (isSearching) {
          response = await api({ requiresAuth: true }).post("/v1/search/potential-customers", searchData, {
            params: { page, ...teamQuery(teamId) },
            signal,
          });
        } else {
          response = await api({ requiresAuth: true }).get("/v1/potential-customers", {
            params: { page, ...teamQuery(teamId) },
            signal,
          });
        }

        if (!potentialCustomerRequestGate.isActive(requestKey)) return;
        commit("setCustomersPerPage", response.data);
        commit("setPotentialCustomerCollectionAuthorization", parseCollectionAuthorization(response));
      } catch (err) {
        if (err.code === "ERR_CANCELED" || !potentialCustomerRequestGate.isActive(requestKey)) return;
        commit("setPotentialCustomerCollectionError", parseApiError(err));
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToSearchPotentialCustomers"),
        });
      } finally {
        if (potentialCustomerRequestGate.isActive(requestKey)) {
          commit("setPotentialCustomersLoading", false);
        }
      }
    },

    searchCustomers: async ({ commit, dispatch, rootGetters }, { page = 1, teamId = null, ...searchData }) => {
      const requestKey = teamScopedCacheKey({
        endpoint: "/v1/customers",
        teamId,
        query: { page, ...searchData },
        actorId: rootGetters.loggedUser?.id,
      });
      const { signal } = customerRequestGate.start(requestKey);

      commit("setCustomersLoading", true);
      commit("setCustomersPerPage", { data: [] });
      commit("setCustomerCollectionAuthorization", null);
      commit("setCustomerCollectionError", null);
      try {
        const hasTeamScope = Array.isArray(teamId) ? teamId.length > 0 : !!teamId;
        const isSearching = hasTeamScope || Object.values(searchData).some(val =>
          (Array.isArray(val) ? val.length > 0 : (val !== "" && val !== null && val !== undefined))
        );

        let response;
        if (isSearching || searchData.export) {
          response = await api({ requiresAuth: true }).post("/v1/search/customers", searchData, {
            params: { page, ...teamQuery(teamId) },
            responseType: searchData.export ? "blob" : "json",
            signal,
          });
        } else {
          response = await api({ requiresAuth: true }).get("/v1/customers", {
            params: { page, ...teamQuery(teamId) },
            signal,
          });
        }

        if (!customerRequestGate.isActive(requestKey)) return;

        if (searchData.export) {
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          const fileURL = window.URL.createObjectURL(blob);
          const fileLink = document.createElement("a");
          fileLink.href = fileURL;
          fileLink.setAttribute("download", "Customers_export.xlsx");
          document.body.appendChild(fileLink);
          fileLink.click();
          document.body.removeChild(fileLink);
          window.URL.revokeObjectURL(fileURL);
        } else {
          commit("setCustomersPerPage", response.data);
          commit("setCustomerCollectionAuthorization", parseCollectionAuthorization(response));
        }
      } catch (err) {
        if (err.code === "ERR_CANCELED" || !customerRequestGate.isActive(requestKey)) return;
        commit("setCustomerCollectionError", parseApiError(err));
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToSearchCustomers"),
        });
      } finally {
        if (customerRequestGate.isActive(requestKey)) {
          commit("setCustomersLoading", false);
        }
      }
    },

    searchLeads: async ({ commit, dispatch, rootGetters }, { page = 1, teamId = null, ...searchData }) => {
      const requestKey = teamScopedCacheKey({
        endpoint: "/v1/lead-customers",
        teamId,
        query: { page, ...searchData },
        actorId: rootGetters.loggedUser?.id,
      });
      const { signal } = leadRequestGate.start(requestKey);
      commit("setLeadsLoading", true);
      commit("setLeadCustomersPerPage", { data: [] });
      commit("setLeadCollectionAuthorization", null);
      commit("setLeadCollectionError", null);
      try {
        if (searchData.export && Object.keys(teamQuery(teamId)).length === 0) {
          throw teamContextRequiredError();
        }
        const hasTeamScope = Array.isArray(teamId) ? teamId.length > 0 : !!teamId;
        const isSearching = hasTeamScope || Object.values(searchData).some(val =>
          (Array.isArray(val) ? val.length > 0 : (val !== "" && val !== null && val !== undefined))
        );

        let response;
        if (isSearching || searchData.export) {
          response = await api({ requiresAuth: true }).post("/v1/search/lead-customers", searchData, {
            params: { page, ...teamQuery(teamId) },
            responseType: searchData.export ? "blob" : "json",
            signal,
          });
        } else {
          response = await api({ requiresAuth: true }).get("/v1/lead-customers", {
            params: { page, ...teamQuery(teamId) },
            signal,
          });
        }

        if (!leadRequestGate.isActive(requestKey)) return;

        if (searchData.export) {
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          const fileURL = window.URL.createObjectURL(blob);
          const fileLink = document.createElement("a");
          fileLink.href = fileURL;
          fileLink.setAttribute("download", "Leads_export.xlsx");
          document.body.appendChild(fileLink);
          fileLink.click();
          document.body.removeChild(fileLink);
          window.URL.revokeObjectURL(fileURL);
        } else {
          commit("setLeadCustomersPerPage", response.data);
          commit("setLeadCollectionAuthorization", parseCollectionAuthorization(response));
        }
      } catch (err) {
        if (err.code === "ERR_CANCELED" || !leadRequestGate.isActive(requestKey)) return;
        commit("setLeadCollectionError", parseApiError(err));
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToSearchLeads"),
        });
      } finally {
        if (leadRequestGate.isActive(requestKey)) commit("setLeadsLoading", false);
      }
    },

    searchCustomerForFamily: async ({ dispatch }, { ...searchData }) =>
      new Promise((resolve) => {
        api({ requiresAuth: true })
          .post(`/v1/filter/family-customers`,
            searchData
          )
          .then(
            (response) => {
              const data = response.data.data;
              resolve(data);
            }, 
          )
          .catch((err) => {
            dispatch("showToast", {
              type: "error",
              message: err.response?.data?.message || t("messages.failedToSearchFamilyCustomers"),
            });
          });
      }),

    searchPolicies: async ({ commit, dispatch, rootGetters }, { page = 1, teamId = null, ...searchData }) => {
      const requestKey = teamScopedCacheKey({
        endpoint: "/v1/policies",
        teamId,
        query: { page, ...searchData },
        actorId: rootGetters.loggedUser?.id,
      });
      const { signal } = policyRequestGate.start(requestKey);
      commit("setPoliciesLoading", true);
      commit("setPoliciesPerPage", { data: [] });
      
      commit("setPolicyCollectionError", null);
      try {
        let response = null;

        const hasTeamScope = Array.isArray(teamId) ? teamId.length > 0 : !!teamId;
        const isSearching =
          hasTeamScope ||
          (searchData.search && searchData.search.trim() !== "") ||
          (searchData.agents && searchData.agents.length > 0) ||
          (searchData.companies && searchData.companies.length > 0) ||
          (searchData.branchCategories && searchData.branchCategories.length > 0) ||
          (searchData.policyStatuses && searchData.policyStatuses.length > 0) ||
          searchData.startDateFrom ||
          searchData.startDateTo ||
          searchData.endDateFrom ||
          searchData.endDateTo ||
          searchData.submitDateFrom ||
          searchData.submitDateTo;

        const isExport = !!(searchData.isExport || searchData.export);
        if (isExport && Object.keys(teamQuery(teamId)).length === 0) {
          throw teamContextRequiredError();
        }

        const payload = { ...searchData };
        delete payload.isExport;
        delete payload.export;

        if (isSearching) {
          response = await api({ requiresAuth: true }).post("/v1/search/policies", payload, {
            params: { page, ...teamQuery(teamId), ...(isExport ? { export: true } : {}) },
            responseType: isExport ? "blob" : undefined,
            signal,
          });
        } else {
          response = await api({ requiresAuth: true }).get("/v1/policies", {
            params: { page, ...teamQuery(teamId), ...(isExport ? { export: true } : {}) },
            responseType: isExport ? "blob" : undefined,
            signal,
          });
        }

        if (!policyRequestGate.isActive(requestKey)) return;

        if (isExport) {
          if (response && response.data) {
            const blob = new Blob([response.data], { type: response.headers && response.headers['content-type'] ? response.headers['content-type'] : 'application/octet-stream' });
            const fileURL = window.URL.createObjectURL(blob);
            const fileLink = document.createElement('a');
            fileLink.href = fileURL;
            fileLink.setAttribute('download', 'Policies_export.xlsx');
            document.body.appendChild(fileLink);
            fileLink.click();
            document.body.removeChild(fileLink);
            window.URL.revokeObjectURL(fileURL);
          }
        } else {
          if (response && response.data) {
            commit("setPoliciesPerPage", response.data); 
          }
        }
      } catch (err) {
        if (err.code === "ERR_CANCELED" || !policyRequestGate.isActive(requestKey)) return;
        commit("setPolicyCollectionError", parseApiError(err));
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToSearchPolicies"),
        });
      } finally {
        if (policyRequestGate.isActive(requestKey)) commit("setPoliciesLoading", false);
      }
    },

    getSearchPoliciesForms: async ({ commit, dispatch }, requestedTeamId) => {
      let teamIds = Array.isArray(requestedTeamId) ? requestedTeamId : [requestedTeamId];
      teamIds = teamIds.map(Number).filter(id => Number.isInteger(id) && id > 0);
      commit("setSearchPoliciesForms", {});
      if (teamIds.length === 0) return;

      const requestKey = `policy-forms:${teamIds.join(',')}`;
      const { signal } = policyFormsRequestGate.start(requestKey);
      try {
        const {
          data: { data },
        } = await api({ requiresAuth: true }).get(
          "/v1/partials/search-policies",
          { params: teamQuery(teamIds), signal }
        );

        if (policyFormsRequestGate.isActive(requestKey)) commit("setSearchPoliciesForms", data);
      } catch (err) {
        if (err.code === "ERR_CANCELED" || !policyFormsRequestGate.isActive(requestKey)) return;
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToLoadSearchPolicyForms"),
        });
      }
    },
  },
};
